import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Svg from 'react-inlinesvg';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import { Row, Col, Spin, message } from 'antd';
import clsx from 'clsx';

import useQuery from 'hooks/useQuery';
import { ALL_PRODUCTS_KEY } from 'hooks/useCategories';

import {
  addProductToCard,
  useGuestCartDetail,
  updateOrderProduct,
  useGuestStoreDetails,
  useGuestProductList,
} from 'api/guest_sdk';
import { Role } from 'api/types/auth';

import StoreLayoutHeader from 'components/StoreLayout/Header';
import Layout from 'components/StoreLayout';
import Footer, { useInIframe } from 'components/Layout/Footer';
import ProductModal from 'components/ProductCard/ProductModal';
import ProductCardStore from 'components/Store/ProductCardStore';
import OwnerAlert from './OwnerAlert';
import CartButton from './CartButton';
import OwnerAlertModal from './OwnerAlertModal';
import StoreHeader from './StoreHeader';
import Filter from './Filter';
import { newLogout, useSignedUser } from 'api/auth';

import { Typography } from 'ui-kit';
import { formatCurrency, currencyToSymbol } from 'utils';
import cartIcon from './cart.svg';

import './Store.scss';
import { reverse } from 'utils/urls';
import { URLS } from 'components/Routes/urls';

// GUEST STORE
const Store = () => {
  const { data: user } = useSignedUser();

  const params = useParams();
  const storeId = useMemo(() => (params.storeId ? Number(params.storeId) : null), [params.storeId]);

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showOwnerAlert, setShowOwnerAlert] = useState(false);

  const {
    data: cartData,
    isLoading: isCartLoading,
    mutate: mutateGuestCart,
  } = useGuestCartDetail({
    storeId,
  });

  const { data: storeData, isLoading: isStoreLoading } = useGuestStoreDetails({ storeId });
  const {
    data: productsData,
    isLoading: isProductsLoading,
    mutate: mutateStoreList,
  } = useGuestProductList({
    storeId,
  });

  const queryParams = useQuery();
  const history = useHistory();
  const location = useLocation();

  const isLoading = isCartLoading || isStoreLoading || isProductsLoading;
  const cartOrders = cartData?.order_products || [];
  const totalQuantity = cartOrders.reduce((acc, item) => acc + item.quantity, 0);
  const [modalItem, setModalItem] = useState(null);

  const isHostPreview = useMemo(() => user?.role === Role.HOST, [user?.role]);

  const {
    store_name,
    is_active,
    is_visible,
    currency: storeCurrency,
    enterprise_info,
  } = storeData || {};
  const handleCartButtonClick = () => setIsCartOpen((prev) => !prev);

  const filterProducts = useMemo(() => {
    const isNotEmpty = Boolean(Object.keys(queryParams)?.length);

    const proxySearch = (searchable, condition) => {
      return searchable.some((val) => condition(val?.toLowerCase()));
    };

    const handleSearch = (query) => {
      return (val) => val?.includes(query.toLowerCase());
    };

    if (isNotEmpty) {
      const { category, query = '' } = queryParams;
      const handleQuery = handleSearch(query);
      return productsData?.filter(({ subcategory, product_name }) => {
        if (category && category !== ALL_PRODUCTS_KEY) {
          return proxySearch([subcategory, product_name], handleQuery) && subcategory === category;
        }
        return proxySearch([subcategory, product_name], handleQuery);
      });
    }
    return productsData;
  }, [productsData?.length, queryParams]);

  const handleSetModalItem = (productId, orderProductId = null) => {
    const queryParams =
      orderProductId === null
        ? `?product=${productId}`
        : `?product=${productId}&cartItem=${orderProductId}`;
    history.push({
      pathname: location.pathname,
      search: queryParams,
    });
  };

  const handleCloseModalItem = () => {
    history.push(location.pathname);
  };

  useEffect(() => {
    const { product, cartItem } = queryParams;
    if (!product) {
      setModalItem(null);
      return;
    }
    const productId = parseInt(product);
    const item = productsData?.find((item) => item?.id === productId);
    if (!item) {
      setModalItem(null);
      return;
    }

    if (cartItem === null) {
      setModalItem({ ...item });
      return;
    }
    const cartItemId = parseInt(cartItem);
    const orderProduct = cartOrders.find((item) => item?.id === cartItemId);

    setModalItem({
      ...item,
      name: item.product_name,
      description: item.description,
      price: _.isUndefined(orderProduct) ? item.price : orderProduct.price,
      image: item.image,
      quantity: item.quantity,
      hasQuantity: item.has_quantity,
      category: item.category,
      subcategory: item.subcategory,
      externalLink: item.external_link,
      affiliateLink: item.affiliate_link,
      preorderBy: item.preorder_by,
      extraFields: item.extra_fields,
      priceType: item.price_type,
      productPrices: item.product_prices.map(
        ({ id, label, hour_label, time_label, price, arrangement, is_variable_price }) => ({
          id,
          label,
          hourLabel: hour_label,
          timeLabel: time_label,
          price,
          arrangement,
          isVariablePrice: is_variable_price,
        }),
      ),
      isRequest: item.is_request,
      isPreorder: item.is_preorder,
      isLocal: item.is_local,
      isExternalLink: item.is_external_link,
      isEarlyCheckin: item.is_early_checkin,
      isLateCheckout: item.is_late_checkout,
      showDeliveryDate: item.show_delivery_date,
      currentQuantity: orderProduct?.quantity,
      deliveryDate: orderProduct?.delivery_date,
      orderproductId: orderProduct?.id,
      note: orderProduct?.note,
      extraFieldValues: orderProduct?.extra_field_values.map((extra_field) => ({
        productField: extra_field.product_field_id,
        value: extra_field.value,
        label: extra_field.label,
      })),
      selectedProductPrice: !_.isEmpty(orderProduct?.product_price)
        ? { ...orderProduct?.product_price, timeLabel: orderProduct?.product_price.time_label }
        : null,
    });
  }, [queryParams, isLoading]);

  const handleAddItem = ({ quantity, deliveryDate, note, extraFields, price, productPriceId }) => {
    if (user?.role === Role.HOST) {
      setShowOwnerAlert(true);
      return;
    }

    if (modalItem.orderproductId) {
      updateOrderProduct({
        orderProductId: modalItem.orderproductId,
        productId: modalItem.id,
        quantity,
        deliveryDate,
        price,
        note,
        extraFields,
        priceType: modalItem.priceType,
        productPriceId,
      })
        .then(() => {
          mutateGuestCart();
          mutateStoreList();
          handleCloseModalItem();
          setIsCartOpen(true);
        })
        .catch((error) => {
          // For serizlizer validation errors
          const fieldErrors = _.get(error, 'non_field_errors', []);

          message.error(error.message || fieldErrors[0]);
        });
    } else {
      addProductToCard({
        productId: modalItem.id,
        quantity,
        deliveryDate,
        price,
        note,
        extraFields,
        priceType: modalItem.priceType,
        productPriceId,
      })
        .then(() => {
          mutateGuestCart();
          mutateStoreList();
          handleCloseModalItem();
          setIsCartOpen(true);
        })
        .catch((error) => {
          // For serizlizer validation errors
          const fieldErrors = _.get(error, 'non_field_errors', []);

          message.error(error.message || fieldErrors[0]);
        });
    }
  };

  const handleLogout = useCallback(
    () =>
      newLogout().then(
        () => (window.location.href = reverse({ url: URLS.GUEST_STORE, params: { storeId } })),
      ),
    [storeId],
  );
  const inFrame = useInIframe();
  return (
    <Spin spinning={isLoading}>
      <div className="Store">
        <Helmet>{store_name && <title>{store_name} | The Host Co</title>}</Helmet>
        <OwnerAlertModal
          visible={showOwnerAlert}
          onCancel={() => setShowOwnerAlert(false)}
          logout={() => {
            setShowOwnerAlert(false);
            handleLogout();
          }}
        />
        <Layout>
          <StoreLayoutHeader
            isCartOpen={isCartOpen}
            isEnterprise={enterprise_info?.is_enabled}
            companyName={enterprise_info?.company_name}
            logo={enterprise_info?.image}
            isLoading={isLoading}
          >
            <CartButton
              handleCartButtonClick={handleCartButtonClick}
              isCartOpen={isCartOpen}
              storeId={storeId}
              totalAmount={totalQuantity}
              onEdit={handleSetModalItem}
              mutateStoreList={mutateStoreList}
              storeCurrency={storeCurrency}
            />
          </StoreLayoutHeader>
          <div className="Store_divider" />
          <Layout.Content className="Store_layout">
            {isHostPreview && <OwnerAlert logout={handleLogout} />}
            {is_active && is_visible ? (
              <>
                {storeData && <StoreHeader store={storeData} />}
                <ProductModal
                  title={modalItem?.name}
                  description={modalItem?.description}
                  amount={modalItem?.currentQuantity ?? 1}
                  price={modalItem?.price}
                  currency={currencyToSymbol(storeCurrency)}
                  imgSrc={modalItem?.image}
                  onAdd={handleAddItem}
                  quantity={modalItem?.quantity}
                  category={modalItem?.category}
                  hasQuantity={modalItem?.hasQuantity}
                  subcategory={modalItem?.subcategory}
                  onCancel={handleCloseModalItem}
                  visible={Boolean(modalItem?.id)}
                  isPreorder={modalItem?.isPreorder}
                  isRequest={modalItem?.isRequest}
                  isLocal={modalItem?.isLocal}
                  isLink={modalItem?.isExternalLink}
                  externalLink={modalItem?.externalLink}
                  affiliateLink={modalItem?.affiliateLink}
                  storeId={storeData?.id}
                  preorderBy={modalItem?.preorderBy}
                  deliveryDate={modalItem?.deliveryDate}
                  note={modalItem?.note}
                  extraFields={modalItem?.extraFields}
                  extraFieldValues={modalItem?.extraFieldValues}
                  priceType={modalItem?.priceType}
                  productPrices={modalItem?.productPrices}
                  isEarlyCheckin={modalItem?.isEarlyCheckin}
                  isLateCheckout={modalItem?.isLateCheckout}
                  selectedProductPrice={modalItem?.selectedProductPrice}
                  showDeliveryDate={modalItem?.showDeliveryDate}
                />
                <div className={clsx('Store-tabs-container', { iframe: inFrame })}>
                  <Filter products={productsData} />
                </div>
                <Row className="Store_row" justify="center">
                  <Col xs={24} lg={24}>
                    <div className="Store_row-list-grid">
                      {filterProducts?.length ? (
                        filterProducts.map((item) => (
                          <ProductCardStore
                            key={item.id}
                            title={item.product_name}
                            quantity={item.quantity}
                            id={item.id}
                            onHandleSetModalId={({ id }) => !isCartOpen && handleSetModalItem(id)}
                            className="Store-productCard"
                            currency={currencyToSymbol(storeData?.currency)}
                            price={formatCurrency(item.price)}
                            category={item.category}
                            isPreorder={item.is_preorder}
                            isRequest={item.is_request}
                            isLink={item.is_external_link}
                            hasQuantity={item.has_quantity}
                            imgSrc={item.image}
                            storeId={storeId}
                            externalLink={item.is_local ? item.affiliate_link : item.external_link}
                            priceType={item.price_type}
                          />
                        ))
                      ) : (
                        <div className="Store_row-list-grid-empty">
                          <Svg src={cartIcon} />
                          <Typography variant="body2" color="textGray">
                            {!queryParams.query
                              ? 'There are no products in this category'
                              : 'There are no matching search results'}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </>
            ) : (
              'Oops! Looks like this store is unavailable. If you need support contact us at support@thehost.co'
            )}
          </Layout.Content>
          <Footer isEnterprise={enterprise_info?.is_enabled} about={enterprise_info?.about} />
        </Layout>
      </div>
    </Spin>
  );
};

export default Store;
